/**
 * <NpsSurvey> — NPS score + optional comment flow (organism, L0.1).
 *
 * Owns local form state (selected score, comment, phase). Side effects are injected:
 * `onSelectScore` / `onSubmitComment`. Notifies the parent modal via `onScoreAccepted` so the
 * header close button can choose dismiss-vs-skip-comment. Fully Storybook-renderable with mocks.
 */
import { useEffect, useState } from "react"
import { StyleSheet } from "react-native-unistyles"

import { Button } from "../../atoms/Buttons/Button"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { Title } from "../../atoms/typography/Title"
import { TextInput } from "../../atoms/TextInput"
import { WaitingText } from "../../atoms/WaitingText"
import { NpsScoreScale } from "../../molecules/NpsScoreScale"

export type NpsSurveyProps = {
    /** Called when the user taps a score — should submit immediately (legacy parity). */
    onSelectScore: (score: number) => Promise<void> | void
    /** Called when the user sends the optional comment after a score was accepted. */
    onSubmitComment: (score: number, comment: string) => Promise<void> | void
    /** Fired once the score API succeeds — parent uses this for close-button routing. */
    onScoreAccepted?: (score: number) => void
    /** Fired once the comment API succeeds (thanks phase) — the parent must then close silently. */
    onCommentSubmitted?: (score: number) => void
    /** Fired when the user taps Fermer on the thanks screen. */
    onFinished?: () => void
    /** External submitting flag (comment submit in flight). */
    commentSubmitting?: boolean
    /** Reset local state when the modal re-opens. */
    resetKey?: string | number | boolean
    testID?: string
}

function commentLabelFor(score: number | null): string {
    if (score == null) return "Qu'est-ce qui justifie votre note ?"
    if (score <= 7) return "Qu'est-ce qui pourrait améliorer votre expérience ?"
    if (score <= 9) return "Qu'est-ce qui vous ferait passer à 10/10 ?"
    return "Qu'est-ce qui vous plaît le plus chez Masteur ?"
}

export function NpsSurvey(props: NpsSurveyProps) {
    const {
        onSelectScore,
        onSubmitComment,
        onScoreAccepted,
        onCommentSubmitted,
        onFinished,
        commentSubmitting = false,
        resetKey,
        testID,
    } = props

    const [selectedScore, setSelectedScore] = useState<number | null>(null)
    const [comment, setComment] = useState("")
    const [scoreAccepted, setScoreAccepted] = useState(false)
    const [scoreSubmitting, setScoreSubmitting] = useState(false)
    const [thanks, setThanks] = useState(false)

    useEffect(() => {
        setSelectedScore(null)
        setComment("")
        setScoreAccepted(false)
        setScoreSubmitting(false)
        setThanks(false)
    }, [resetKey])

    const handleSelect = async (score: number) => {
        if (scoreSubmitting || scoreAccepted) return
        setSelectedScore(score)
        if (comment.trim()) setComment("")
        setScoreSubmitting(true)
        try {
            await onSelectScore(score)
            setScoreAccepted(true)
            onScoreAccepted?.(score)
        } catch {
            // Keep score unaccepted so the user can retry (API / network failure).
        } finally {
            setScoreSubmitting(false)
        }
    }

    const handleCommentSubmit = async () => {
        if (!scoreAccepted || selectedScore == null || !comment.trim() || commentSubmitting) return
        try {
            await onSubmitComment(selectedScore, comment.trim())
            setThanks(true)
            onCommentSubmitted?.(selectedScore)
        } catch {
            // Leave the comment form open for retry.
        }
    }

    if (thanks) {
        return (
            <Stack gap="4" align="center" testID={testID} style={styles.root}>
                <Title center size="text-xl">
                    Merci pour votre retour !
                </Title>
                <Text color="text-muted" center>
                    Votre avis nous aide à améliorer continuellement Masteur pour vous et votre enfant.
                </Text>
                <Button label="Fermer" onPress={() => onFinished?.()} />
            </Stack>
        )
    }

    return (
        <Stack gap="4" align="center" testID={testID} style={styles.root}>
            <Title center size="text-lg">
                Recommanderiez-vous Masteur à d&apos;autres parents ?
            </Title>

            <Row justify="between" style={styles.labels}>
                <Text size="text-xs" color="text-muted" style={styles.labelLeft}>
                    Non, certainement pas
                </Text>
                <Text size="text-xs" color="text-muted" style={styles.labelRight}>
                    Oui, certainement
                </Text>
            </Row>

            <NpsScoreScale value={selectedScore} onSelect={handleSelect} disabled={scoreSubmitting || scoreAccepted} />

            {scoreSubmitting && !scoreAccepted && (
                <WaitingText color="text-muted" weight="regular" numberOfLines={2}>
                    Envoi de votre note...
                </WaitingText>
            )}

            {scoreAccepted && (
                <Stack gap="3" style={styles.full}>
                    <TextInput
                        label={commentLabelFor(selectedScore)}
                        value={comment}
                        onChangeText={setComment}
                        placeholder="Partagez votre expérience avec Masteur..."
                        multiline
                        numberOfLines={4}
                        disabled={commentSubmitting}
                    />
                    <Button
                        label="Envoyer"
                        onPress={handleCommentSubmit}
                        disabled={!comment.trim() || commentSubmitting}
                        loading={commentSubmitting}
                        fullWidth
                    />
                </Stack>
            )}
        </Stack>
    )
}

const styles = StyleSheet.create(() => ({
    // No extra padding — `<Modal>` body already pads; keeps the fit-content dialog compact.
    root: {
        width: "100%",
    },
    labels: {
        width: "100%",
    },
    labelLeft: {
        maxWidth: "40%",
    },
    labelRight: {
        maxWidth: "40%",
        textAlign: "right" as const,
    },
    full: {
        width: "100%",
    },
}))
